import type { KeyboardEvent } from 'react';
import { format } from 'date-fns';

import { cn } from '@poseidon/components/cn';
import { StepIcon } from '@poseidon/components/HistoryStep';

import type { HistoryCardSummary } from '~/components/Sequence/sequenceHistory';

interface Props {
  /** The entry's id — what opening it reports, and what the popover anchors to. */
  entryId: string;
  summary: HistoryCardSummary;
  /** Row centre (content px) and the spine's x — the card is centred on the spine. */
  top: number;
  spineX: number;
  cardWidth: number;
  open: boolean;
  onOpen: (entryId: string, anchor: Element) => void;
}

/**
 * One history entry on the sequence canvas: a compact card ON the spine — who did what, when, and the first
 * words of anything said. Clicking it opens the full entry ({@link SequenceHistoryPopover}). `data-no-pan`, so
 * pressing a card opens it rather than starting a pan.
 */
export function SequenceHistoryCard({ entryId, summary, top, spineX, cardWidth, open, onOpen }: Props) {
  const openFrom = (el: Element) => onOpen(entryId, el);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    openFrom(e.currentTarget);
  };
  return (
    <div
      data-no-pan
      data-history-card={entryId}
      role="button"
      tabIndex={0}
      aria-label={`${summary.title} — open`}
      onClick={(e) => openFrom(e.currentTarget)}
      onKeyDown={onKeyDown}
      className={cn(
        'absolute flex cursor-pointer items-start gap-2 rounded-xl border bg-white px-2.5 py-1.5 text-left shadow-xs transition-colors',
        'border-lynch-200 hover:border-lynch-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800',
        open && 'border-blue-300 ring-2 ring-blue-200 dark:border-blue-500/60 dark:ring-blue-500/30',
      )}
      // Centred ON the spine, like the graph's own pills — so it reads as part of the line, and stays whole
      // in a narrow panel wherever the spine has been panned to.
      style={{ left: spineX, top, width: cardWidth, transform: 'translate(-50%, -50%)' }}>
      <span aria-hidden className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-lynch-500">
        <StepIcon stepType={summary.stepType} size={13} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700 dark:text-slate-200">
            {summary.title}
          </span>
          <span className="shrink-0 text-[10px] text-lynch-400 dark:text-lynch-500">{format(summary.at, 'MMM d')}</span>
        </span>
        {summary.snippet != null && (
          <span className="block truncate text-xs text-lynch-500 dark:text-lynch-400">{summary.snippet}</span>
        )}
      </span>
    </div>
  );
}
